import { Trophy, Users, Target, Award, History } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_soccer-pro-21/artifacts/6hs5jyaw_image-removebg-preview.png";
const CROWD_IMG = "https://images.unsplash.com/photo-1705593973313-75de7bf95b56?crop=entropy&cs=srgb&fm=jpg&q=85";

const AboutPage = () => {
  const executiveCommittee = [
    { name: "Everton Gonsalves", position: "President", photo: "https://placehold.co/200x200/09090b/FFF?text=EG" },
    { name: "Rowan Benjamin", position: "Vice President", photo: "https://placehold.co/200x200/09090b/FFF?text=RB" },
    { name: "Gordon Derrick", position: "General Secretary", photo: "https://placehold.co/200x200/09090b/FFF?text=GD" },
    { name: "Rawlston Aaron", position: "Treasurer", photo: "https://placehold.co/200x200/09090b/FFF?text=RA" },
  ];

  const milestones = [
    { year: "1928", event: "ABFA Founded", desc: "Established as the governing body for football in Antigua and Barbuda" },
    { year: "1970", event: "FIFA Membership", desc: "Became a member of the Fédération Internationale de Football Association" },
    { year: "1978", event: "CFU Membership", desc: "Joined the Caribbean Football Union" },
    { year: "2001", event: "Technical Centre", desc: "Opened the ABFA Technical Centre for player development" },
  ];

  return (
    <div data-testid="about-page">
      {/* Hero Section */}
      <section 
        className="relative h-[50vh] flex items-center justify-center bg-cover bg-center"
        style={{ backgroundImage: `url(${CROWD_IMG})` }}
        data-testid="about-hero"
      >
        <div className="absolute inset-0 hero-overlay"></div>
        <div className="relative z-10 text-center text-white px-4">
          <h1 className="font-heading text-5xl md:text-7xl font-bold uppercase tracking-tight mb-4 animate-fade-in">
            About ABFA
          </h1>
          <p className="text-lg md:text-xl text-gray-300 animate-fade-in-delay-1">
            The Heart of Caribbean Football Since 1928
          </p>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-16 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <img src={LOGO_URL} alt="ABFA Logo" className="w-64 h-64 mx-auto lg:mx-0" />
            </div>
            <div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold uppercase text-[#09090b] mb-6">
                Our Mission
              </h2>
              <p className="text-gray-600 text-lg leading-relaxed mb-6">
                The Antigua and Barbuda Football Association (ABFA) is committed to developing, promoting, and governing the sport of football throughout Antigua and Barbuda. We strive to provide opportunities for all citizens to participate in football at all levels.
              </p>
              <p className="text-gray-600 text-lg leading-relaxed">
                Our vision is to make Antigua and Barbuda a recognized force in Caribbean and international football, nurturing talent from grassroots to the national team level.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <h2 className="font-heading text-3xl font-bold uppercase text-center text-[#09090b] mb-12">
            Our Core Values
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { icon: Trophy, title: "Excellence", desc: "Striving for the highest standards in everything we do" },
              { icon: Users, title: "Unity", desc: "Bringing together communities through the beautiful game" },
              { icon: Target, title: "Integrity", desc: "Operating with honesty and transparency" },
              { icon: Award, title: "Development", desc: "Investing in youth and grassroots football" },
            ].map((value, i) => (
              <div key={i} className="text-center p-6 bg-white hover:shadow-lg transition-shadow" data-testid={`value-card-${i}`}>
                <div className="w-16 h-16 mx-auto mb-4 bg-[#FFC72C] flex items-center justify-center">
                  <value.icon className="w-8 h-8 text-black" />
                </div>
                <h3 className="font-heading font-bold text-xl uppercase text-[#09090b] mb-2">{value.title}</h3>
                <p className="text-gray-600">{value.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* History Timeline */}
      <section className="py-16 px-4 md:px-8">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-heading text-3xl font-bold uppercase text-center text-[#09090b] mb-12">
            <History className="inline-block w-8 h-8 mr-2 text-[#FFC72C]" />
            Our History
          </h2>
          <div className="space-y-8">
            {milestones.map((milestone, i) => (
              <div key={i} className="flex gap-6 items-start" data-testid={`milestone-${i}`}>
                <div className="flex-shrink-0 w-24 h-24 bg-[#09090b] text-white flex items-center justify-center">
                  <span className="font-heading font-bold text-2xl">{milestone.year}</span>
                </div>
                <div className="flex-1 pt-2">
                  <h3 className="font-heading font-bold text-xl uppercase text-[#09090b] mb-2">{milestone.event}</h3>
                  <p className="text-gray-600">{milestone.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Executive Committee */}
      <section className="py-16 bg-[#09090b]">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <h2 className="font-heading text-3xl font-bold uppercase text-center text-white mb-12">
            Executive Committee
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {executiveCommittee.map((member, i) => (
              <div key={i} className="text-center" data-testid={`committee-member-${i}`}>
                <div className="w-40 h-40 mx-auto mb-4 overflow-hidden">
                  <img 
                    src={member.photo} 
                    alt={member.name}
                    className="w-full h-full object-cover grayscale hover:grayscale-0 transition-all"
                  />
                </div>
                <h3 className="font-heading font-bold text-lg uppercase text-white">{member.name}</h3>
                <p className="text-[#FFC72C]">{member.position}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Affiliations */}
      <section className="py-16 px-4 md:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-heading text-3xl font-bold uppercase text-[#09090b] mb-8">
            Affiliations
          </h2>
          <p className="text-gray-600 text-lg mb-8">
            ABFA is proudly affiliated with major international and regional football organizations.
          </p>
          <div className="flex flex-wrap justify-center gap-8">
            {["FIFA", "CONCACAF", "CFU"].map((org, i) => (
              <div key={i} className="px-8 py-4 bg-gray-100 font-heading font-bold text-xl text-[#09090b]">
                {org}
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
